import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { formatTime } from '../../utils/format';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { timerStartFromTime } from './timerModel';

/**
 * FIX-TIMER-START-EDIT: the start time of a running / paused timer, as a
 * button. Click -> a time field (hour only, the same day) -> save. The server
 * refuses a future time, an overlap with another log and a locked month; its
 * message is shown as is. After a save the timer, the attendance line
 * ("תיעדת X מתוך Y") and the logs refresh at once.
 */
export function TimerStartEdit({ timerId, startedAt, className = '' }: { timerId: string; startedAt: string; className?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const open = () => {
    setValue(formatTime(startedAt, ''));
    setEditing(true);
  };

  const save = async () => {
    const next = timerStartFromTime(startedAt, value);
    if (!next) { toast('error', t('timeTasks.timers.editStartFailed')); return; }
    if (next === new Date(startedAt).toISOString()) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.editTimerStart(timerId, next);
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['timer-status'] });
      void qc.invalidateQueries({ queryKey: ['attendance-status'] });
      void qc.invalidateQueries({ queryKey: ['work-logs'] });
    } catch (err) {
      toast('error', apiErrorMessage(err, t('timeTasks.timers.editStartFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={open}
        title={t('timeTasks.timers.editStart')}
        aria-label={t('timeTasks.timers.editStart')}
        data-testid="timer-start-edit"
        className={`whitespace-nowrap tabular-nums underline decoration-dotted underline-offset-2 hover:text-text ${className}`}
      >
        {t('timeTasks.timers.startedAt', { time: formatTime(startedAt) })}
      </button>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <input
        type="time"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') setEditing(false); }}
        aria-label={t('timeTasks.timers.editStart')}
        data-testid="timer-start-input"
        dir="ltr"
        // The user just clicked the start time to edit it: the field takes the focus.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        className="rounded border border-border bg-surface px-1 py-0.5 text-xs tabular-nums"
      />
      <button type="button" onClick={() => void save()} disabled={busy} data-testid="timer-start-save" className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent-soft-text">
        {t('timeTasks.timers.editStartSave')}
      </button>
      <button type="button" onClick={() => setEditing(false)} disabled={busy} className="text-xs text-text-2 hover:underline">
        {t('timeTasks.timers.editStartCancel')}
      </button>
    </span>
  );
}
